import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { downloadApiFile } from "../lib/downloadApiFile";
import { PageHeader, SectionCard, Pill, StatePill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { ChevronDown, Download, Plus } from "lucide-react";
import DataTableBlock from "../components/DataTableBlock";
import DataTableExportButton from "../components/DataTableExportButton";
import { useBootstrapList } from "../hooks/useBootstrapList";
import { useBootstrapWS } from "../hooks/useBootstrapWS";
import { useWS } from "../context/WebSocketContext";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { STACK_COLUMNS } from "./stackColumns";
import AdminEntityActions from "../components/AdminEntityActions";
import { mapNumberFields } from "../lib/numberInput";
import { useLookups } from "../context/LookupOptionsContext";
import { useAuth } from "../context/AuthContext";
import { isProductionLoadingSupervisorRole } from "../lib/dashboardRole";

const ALL = "__all__";

function batchStatusLabel(status) {
  if (status === "UNLOADING_ACTIVE") return "Unloading";
  if (status === "MILLING_ONLY") return "Milling";
  if (status === "ARCHIVED_COMPLETED") return "Archived";
  if (status === "PLANNED") return "Planned";
  return status || "—";
}

function formatStockWhen(iso) {
  if (!iso) return "—";
  const s = String(iso);
  if (s.length >= 16) return `${s.slice(0, 10)} ${s.slice(11, 16)}`;
  return s.slice(0, 10) || "—";
}

function PackedProductStockPanel() {
  const { subscribe } = useWS();
  const [rows, setRows] = useState([]);
  const [batches, setBatches] = useState([]);
  const [items, setItems] = useState([]);
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movementOpen, setMovementOpen] = useState(false);
  const [movementLoading, setMovementLoading] = useState(false);
  const [movementExporting, setMovementExporting] = useState(false);
  const [movementSide, setMovementSide] = useState("both");
  const [movementRow, setMovementRow] = useState(null);
  const [movements, setMovements] = useState(null);
  const [filters, setFilters] = useState({
    batch_id: ALL,
    item_id: ALL,
    crop_id: ALL,
    batch_status: ALL,
    batch_code: "",
    item_code: "",
    weight_per_bag: "",
    date_from: "",
    date_to: "",
    positive_only: false,
  });

  const loadMeta = useCallback(async () => {
    try {
      const [b, i, c] = await Promise.all([
        api.get("/production/batches"),
        api.get("/production/masters/items"),
        api.get("/production/masters/crops"),
      ]);
      setBatches(b.data?.results || []);
      setItems(i.data?.results || []);
      setCrops(c.data?.results || []);
    } catch (e) {
      toast.error(extractError(e));
    }
  }, []);

  const loadStock = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.batch_id !== ALL) params.batch_id = Number(filters.batch_id);
      if (filters.item_id !== ALL) params.item_id = Number(filters.item_id);
      if (filters.crop_id !== ALL) params.crop_id = Number(filters.crop_id);
      if (filters.batch_status !== ALL) params.batch_status = filters.batch_status;
      if (filters.batch_code.trim()) params.batch_code = filters.batch_code.trim();
      if (filters.item_code.trim()) params.item_code = filters.item_code.trim();
      if (filters.weight_per_bag.trim()) params.weight_per_bag = Number(filters.weight_per_bag);
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.positive_only) params.positive_only = 1;
      const { data } = await api.get("/stacks/product-stock", { params });
      setRows(data?.results || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  useEffect(() => {
    const unsubs = [
      subscribe("production", loadStock),
      subscribe("production_batch", loadStock),
      subscribe("outward_dispatch", loadStock),
    ];
    return () => unsubs.forEach((u) => u?.());
  }, [subscribe, loadStock]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.opening += Number(r.bags_opening || 0);
        acc.in += Number(r.bags_in || 0);
        acc.out += Number(r.bags_out || 0);
        acc.closing += Number(r.bags_closing || 0);
        acc.openingQtl += Number(r.weight_opening_qtl || 0);
        acc.inQtl += Number(r.weight_in_qtl || 0);
        acc.outQtl += Number(r.weight_out_qtl || 0);
        acc.closingQtl += Number(r.weight_closing_qtl || 0);
        return acc;
      },
      { opening: 0, in: 0, out: 0, closing: 0, openingQtl: 0, inQtl: 0, outQtl: 0, closingQtl: 0 },
    );
  }, [rows]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const applyAsOfDate = (isoDate) => {
    if (!isoDate) return;
    setFilters((f) => ({ ...f, date_from: isoDate, date_to: isoDate }));
  };

  const stockExportParams = useMemo(() => {
    const params = {};
    if (filters.batch_id !== ALL) params.batch_id = Number(filters.batch_id);
    if (filters.item_id !== ALL) params.item_id = Number(filters.item_id);
    if (filters.crop_id !== ALL) params.crop_id = Number(filters.crop_id);
    if (filters.batch_status !== ALL) params.batch_status = filters.batch_status;
    if (filters.batch_code.trim()) params.batch_code = filters.batch_code.trim();
    if (filters.item_code.trim()) params.item_code = filters.item_code.trim();
    if (filters.weight_per_bag.trim()) params.weight_per_bag = Number(filters.weight_per_bag);
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.positive_only) params.positive_only = 1;
    return params;
  }, [filters]);

  const [exporting, setExporting] = useState(false);
  const handleExport = async (mode) => {
    if (!rows.length) {
      toast.error("No stock rows to export");
      return;
    }
    setExporting(true);
    try {
      const stamp = Date.now();
      if (mode === "excel") {
        await downloadApiFile(
          "/stacks/product-stock/export/excel",
          `packed-stock-${stamp}.csv`,
          stockExportParams,
        );
        toast.success("Excel (CSV) downloaded");
      } else {
        await downloadApiFile(
          "/stacks/product-stock/export/pdf",
          `packed-stock-${stamp}.pdf`,
          stockExportParams,
        );
        toast.success("PDF downloaded");
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setExporting(false);
    }
  };

  const periodLabel = filters.date_from && filters.date_to
    ? (filters.date_from === filters.date_to
      ? `As of ${filters.date_to}`
      : `${filters.date_from} → ${filters.date_to}`)
    : filters.date_to
      ? `Through ${filters.date_to}`
      : filters.date_from
        ? `From ${filters.date_from}`
        : "All dates";

  const movementParams = useCallback((row, side = "both") => {
    const params = {
      batch_id: row.batch_id,
      item_id: row.item_id,
      weight_per_bag: row.weight_per_bag,
      side,
    };
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    return params;
  }, [filters.date_from, filters.date_to]);

  const openMovements = useCallback(async (row, side = "both") => {
    if (!row?.batch_id || !row?.item_id) return;
    setMovementRow(row);
    setMovementSide(side);
    setMovementOpen(true);
    setMovementLoading(true);
    setMovements(null);
    try {
      const { data } = await api.get("/stacks/product-stock/movements", {
        params: movementParams(row, side),
      });
      setMovements(data);
    } catch (e) {
      toast.error(extractError(e));
      setMovementOpen(false);
    } finally {
      setMovementLoading(false);
    }
  }, [movementParams]);

  const exportMovements = async (mode) => {
    if (!movementRow) return;
    setMovementExporting(true);
    try {
      const stamp = Date.now();
      const params = movementParams(movementRow, movementSide);
      const base = `${movementRow.batch_code || "batch"}-${movementRow.item_code || "item"}-${movementSide}`;
      if (mode === "excel") {
        await downloadApiFile(
          "/stacks/product-stock/movements/export/excel",
          `stock-movements-${base}-${stamp}.csv`,
          params,
        );
        toast.success("Excel (CSV) downloaded");
      } else {
        await downloadApiFile(
          "/stacks/product-stock/movements/export/pdf",
          `stock-movements-${base}-${stamp}.pdf`,
          params,
        );
        toast.success("PDF downloaded");
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setMovementExporting(false);
    }
  };

  const qtl = (n) => {
    const v = Number(n);
    return Number.isFinite(v) ? v.toFixed(2) : "—";
  };

  const clickableNum =
    "underline decoration-dotted underline-offset-2 hover:text-primary cursor-pointer";

  return (
    <div className="space-y-4" data-testid="packed-product-stock">
      <SectionCard title="Filters" testId="packed-stock-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Batch</Label>
            <Select value={filters.batch_id} onValueChange={(v) => setFilter("batch_id", v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="All batches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All batches</SelectItem>
                {batches.map((b) => (
                  <SelectItem key={b.id} value={String(b.id)}>
                    {b.code} · {batchStatusLabel(b.status)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Variety / item</Label>
            <Select value={filters.item_id} onValueChange={(v) => setFilter("item_id", v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="All items" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All items</SelectItem>
                {items.map((it) => (
                  <SelectItem key={it.id} value={String(it.id)}>{it.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Crop</Label>
            <Select value={filters.crop_id} onValueChange={(v) => setFilter("crop_id", v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="All crops" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All crops</SelectItem>
                {crops.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Batch status</Label>
            <Select value={filters.batch_status} onValueChange={(v) => setFilter("batch_status", v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                <SelectItem value="UNLOADING_ACTIVE">Unloading</SelectItem>
                <SelectItem value="MILLING_ONLY">Milling</SelectItem>
                <SelectItem value="ARCHIVED_COMPLETED">Archived</SelectItem>
                <SelectItem value="PLANNED">Planned</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Batch code contains</Label>
            <Input
              className="mt-1"
              value={filters.batch_code}
              onChange={(e) => setFilter("batch_code", e.target.value)}
              placeholder="e.g. 260715"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Item code / name</Label>
            <Input
              className="mt-1"
              value={filters.item_code}
              onChange={(e) => setFilter("item_code", e.target.value)}
              placeholder="e.g. WAND"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Pack size (kg)</Label>
            <Input
              className="mt-1"
              type="number"
              step="0.01"
              value={filters.weight_per_bag}
              onChange={(e) => setFilter("weight_per_bag", e.target.value)}
              placeholder="e.g. 50 or 25"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              As of date (back-dated closing)
            </Label>
            <Input
              className="mt-1"
              type="date"
              value={filters.date_from && filters.date_from === filters.date_to ? filters.date_to : ""}
              onChange={(e) => applyAsOfDate(e.target.value)}
              data-testid="stock-as-of-date"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Sets From = To for that day (opening → in → out → closing).
            </p>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">From date</Label>
            <Input
              className="mt-1"
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilter("date_from", e.target.value)}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">To date</Label>
            <Input
              className="mt-1"
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilter("date_to", e.target.value)}
            />
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none pb-2">
              <input
                type="checkbox"
                checked={filters.positive_only}
                onChange={(e) => setFilter("positive_only", e.target.checked)}
              />
              Closing &gt; 0 only
            </label>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <Button type="button" variant="outline" onClick={() => setFilters({
              batch_id: ALL, item_id: ALL, crop_id: ALL, batch_status: ALL,
              batch_code: "", item_code: "", weight_per_bag: "", date_from: "", date_to: "", positive_only: false,
            })}>
              Clear filters
            </Button>
            <Button type="button" className="bg-bansal-sage hover:bg-bansal-sageDark text-white" onClick={loadStock}>
              Refresh
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={exporting || loading}
                  data-testid="packed-stock-export"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {exporting ? "Exporting…" : "Export"}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport("excel")}>
                  Export Excel (CSV)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("pdf")}>
                  Export PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </SectionCard>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Opening", bags: totals.opening, qtl: totals.openingQtl },
          { label: "In (period)", bags: totals.in, qtl: totals.inQtl },
          { label: "Out (period)", bags: totals.out, qtl: totals.outQtl },
          { label: "Closing", bags: totals.closing, qtl: totals.closingQtl, highlight: true },
        ].map((tile) => (
          <div key={tile.label} className="rounded-md border border-border bg-card px-4 py-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{tile.label}</p>
            <p className={`text-xl font-semibold tabular ${tile.highlight ? "text-primary" : ""}`}>
              {tile.bags} bags
            </p>
            <p className="text-xs text-muted-foreground tabular">{qtl(tile.qtl)} qtl</p>
          </div>
        ))}
      </div>

      <SectionCard
        title={`Packed stock register · ${periodLabel} (${rows.length} line${rows.length === 1 ? "" : "s"})`}
        testId="packed-stock-table"
      >
        <p className="text-xs text-muted-foreground mb-3">
          Opening = stock before From. In/Out = packing &amp; loading in the period.
          Closing = Opening + In − Out. Click In, Out, or a batch code to see packing logs and dispatches for that line.
          Wand 50 kg and Wand 25 kg are separate lines.
          {" "}
          <Link to="/sales/link" className="text-primary underline">Dispatch remaining stock</Link>
          {" · "}
          <Link to="/production/packing-register" className="text-primary underline">Log packaging</Link>
        </p>
        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No packed stock matches these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1100px]">
              <thead className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 px-2" rowSpan={2}>Batch</th>
                  <th className="text-left py-2 px-2" rowSpan={2}>Status</th>
                  <th className="text-left py-2 px-2" rowSpan={2}>Crop / type</th>
                  <th className="text-left py-2 px-2" rowSpan={2}>Variety (item)</th>
                  <th className="text-right py-2 px-2" rowSpan={2}>Kg/bag</th>
                  <th className="text-center py-2 px-2 border-l border-border" colSpan={2}>Opening</th>
                  <th className="text-center py-2 px-2 border-l border-border" colSpan={2}>In</th>
                  <th className="text-center py-2 px-2 border-l border-border" colSpan={2}>Out</th>
                  <th className="text-center py-2 px-2 border-l border-border" colSpan={2}>Closing</th>
                </tr>
                <tr>
                  {["Bags", "Qtl", "Bags", "Qtl", "Bags", "Qtl", "Bags", "Qtl"].map((h, i) => (
                    <th
                      key={`${h}-${i}`}
                      className={`text-right py-1 px-2 ${i % 2 === 0 ? "border-l border-border" : ""}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const closingNeg = Number(r.bags_closing) < 0;
                  return (
                  <tr key={`${r.batch_id}-${r.item_id}-${r.weight_per_bag}`} className="border-b border-border/50 hover:bg-accent/60">
                    <td className="py-2 px-2 font-mono text-xs font-semibold">
                      <button
                        type="button"
                        className={`${clickableNum} font-mono text-xs font-semibold`}
                        onClick={() => openMovements(r, "both")}
                        title="View In & Out log"
                      >
                        {r.batch_code}
                      </button>
                    </td>
                    <td className="py-2 px-2"><StatePill state={r.batch_status} /></td>
                    <td className="py-2 px-2 text-xs">{r.crop_name} · {r.type_name}</td>
                    <td className="py-2 px-2">
                      <span className="font-medium">{r.item_name}</span>
                      <span className="text-xs text-muted-foreground ml-1">({r.item_code})</span>
                    </td>
                    <td className="py-2 px-2 text-right tabular">{r.weight_per_bag}</td>
                    <td className="py-2 px-2 text-right tabular border-l border-border">{r.bags_opening ?? 0}</td>
                    <td className="py-2 px-2 text-right tabular text-muted-foreground">{qtl(r.weight_opening_qtl)}</td>
                    <td className="py-2 px-2 text-right tabular border-l border-border">
                      <button
                        type="button"
                        className={`${clickableNum} tabular`}
                        onClick={() => openMovements(r, "in")}
                        title="View packing / In log"
                      >
                        {r.bags_in}
                      </button>
                    </td>
                    <td className="py-2 px-2 text-right tabular text-muted-foreground">{qtl(r.weight_in_qtl)}</td>
                    <td className="py-2 px-2 text-right tabular border-l border-border">
                      <button
                        type="button"
                        className={`${clickableNum} tabular`}
                        onClick={() => openMovements(r, "out")}
                        title="View dispatch / Out log"
                      >
                        {r.bags_out}
                      </button>
                    </td>
                    <td className="py-2 px-2 text-right tabular text-muted-foreground">{qtl(r.weight_out_qtl)}</td>
                    <td className={`py-2 px-2 text-right tabular font-semibold border-l border-border ${closingNeg ? "text-red-600" : ""}`}>
                      <button
                        type="button"
                        className={`${clickableNum} tabular font-semibold ${closingNeg ? "text-red-600" : ""}`}
                        onClick={() => openMovements(r, "both")}
                        title="View In & Out log"
                      >
                        {r.bags_closing}
                      </button>
                    </td>
                    <td className={`py-2 px-2 text-right tabular font-semibold ${closingNeg ? "text-red-600" : "text-primary"}`}>
                      {qtl(r.weight_closing_qtl)}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-muted/40 font-medium">
                  <td className="py-2 px-2" colSpan={5}>Filtered totals</td>
                  <td className="py-2 px-2 text-right tabular border-l border-border">{totals.opening}</td>
                  <td className="py-2 px-2 text-right tabular">{qtl(totals.openingQtl)}</td>
                  <td className="py-2 px-2 text-right tabular border-l border-border">{totals.in}</td>
                  <td className="py-2 px-2 text-right tabular">{qtl(totals.inQtl)}</td>
                  <td className="py-2 px-2 text-right tabular border-l border-border">{totals.out}</td>
                  <td className="py-2 px-2 text-right tabular">{qtl(totals.outQtl)}</td>
                  <td className="py-2 px-2 text-right tabular border-l border-border">{totals.closing}</td>
                  <td className="py-2 px-2 text-right tabular text-primary">{qtl(totals.closingQtl)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </SectionCard>

      <Dialog open={movementOpen} onOpenChange={setMovementOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Stock movement log
              {movementSide === "in" ? " · In" : movementSide === "out" ? " · Out" : ""}
            </DialogTitle>
            <DialogDescription>
              {movementRow
                ? `${movementRow.batch_code} · ${movementRow.item_name} (${movementRow.item_code}) · ${movementRow.weight_per_bag} kg/bag · ${periodLabel}`
                : "Packing and dispatch lines for this stock row."}
            </DialogDescription>
          </DialogHeader>

          {movementRow && (
            <div className="flex flex-wrap gap-2 text-xs items-center">
              {[
                { key: "both", label: "In & Out" },
                { key: "in", label: "In only" },
                { key: "out", label: "Out only" },
              ].map((tab) => (
                <Button
                  key={tab.key}
                  type="button"
                  size="sm"
                  variant={movementSide === tab.key ? "default" : "outline"}
                  className={movementSide === tab.key ? "bg-bansal-sage hover:bg-bansal-sageDark text-white" : ""}
                  onClick={() => openMovements(movementRow, tab.key)}
                >
                  {tab.label}
                </Button>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={movementExporting || movementLoading || !movements}
                    data-testid="stock-movement-export"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    {movementExporting ? "Exporting…" : "Export"}
                    <ChevronDown className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => exportMovements("excel")}>
                    Export Excel (CSV)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportMovements("pdf")}>
                    Export PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Link
                to="/production/packing-register"
                className="ml-auto self-center text-primary underline"
              >
                Packing register
              </Link>
              <Link to="/sales/loading" className="self-center text-primary underline">
                Loading
              </Link>
            </div>
          )}

          {movementLoading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading movements…</p>
          ) : movements ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="rounded-md border border-border px-3 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">In (period)</p>
                  <p className="font-semibold tabular">{movements.totals?.bags_in ?? 0} bags</p>
                </div>
                <div className="rounded-md border border-border px-3 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Out (period)</p>
                  <p className="font-semibold tabular">{movements.totals?.bags_out ?? 0} bags</p>
                </div>
                <div className="rounded-md border border-border px-3 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Net (In − Out)</p>
                  <p className={`font-semibold tabular ${Number(movements.totals?.bags_closing) < 0 ? "text-red-600" : ""}`}>
                    {movements.totals?.bags_closing ?? 0} bags
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground -mt-2">
                Totals always include both In and Out for the period. Tabs only change which detail table is shown / exported.
              </p>

              {(movementSide === "in" || movementSide === "both") && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">
                    In — packaging / opening stock ({movements.in_entries?.length || 0})
                  </h4>
                  {!movements.in_entries?.length ? (
                    <p className="text-xs text-muted-foreground py-2">No packing logs in this period for this line.</p>
                  ) : (
                    <div className="overflow-x-auto border border-border rounded-md">
                      <table className="w-full text-xs min-w-[640px]">
                        <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
                          <tr>
                            <th className="text-left py-2 px-2">When</th>
                            <th className="text-left py-2 px-2">Kind</th>
                            <th className="text-right py-2 px-2">Bags</th>
                            <th className="text-right py-2 px-2">Qtl</th>
                            <th className="text-left py-2 px-2">By</th>
                            <th className="text-left py-2 px-2">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {movements.in_entries.map((e) => (
                            <tr key={`in-${e.id}`} className="border-t border-border/60">
                              <td className="py-1.5 px-2 tabular whitespace-nowrap">{formatStockWhen(e.at)}</td>
                              <td className="py-1.5 px-2">{e.entry_kind_label || e.entry_kind}</td>
                              <td className="py-1.5 px-2 text-right tabular font-medium">{e.bags}</td>
                              <td className="py-1.5 px-2 text-right tabular text-muted-foreground">{qtl(e.weight_qtl)}</td>
                              <td className="py-1.5 px-2">{e.created_by || "—"}</td>
                              <td className="py-1.5 px-2 text-muted-foreground max-w-[220px] truncate" title={e.notes || ""}>
                                {e.notes || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {(movementSide === "out" || movementSide === "both") && (
                <div>
                  <h4 className="text-sm font-semibold mb-2">
                    Out — finalized dispatches ({movements.out_entries?.length || 0})
                  </h4>
                  {!movements.out_entries?.length ? (
                    <p className="text-xs text-muted-foreground py-2">No finalized outs in this period for this line.</p>
                  ) : (
                    <div className="overflow-x-auto border border-border rounded-md">
                      <table className="w-full text-xs min-w-[720px]">
                        <thead className="bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
                          <tr>
                            <th className="text-left py-2 px-2">When</th>
                            <th className="text-left py-2 px-2">Dispatch</th>
                            <th className="text-left py-2 px-2">Vehicle</th>
                            <th className="text-left py-2 px-2">Party</th>
                            <th className="text-left py-2 px-2">State</th>
                            <th className="text-right py-2 px-2">Bags</th>
                            <th className="text-right py-2 px-2">Qtl</th>
                            <th className="text-left py-2 px-2">Loaded by</th>
                          </tr>
                        </thead>
                        <tbody>
                          {movements.out_entries.map((e) => (
                            <tr key={`out-${e.source}-${e.id}`} className="border-t border-border/60">
                              <td className="py-1.5 px-2 tabular whitespace-nowrap">{formatStockWhen(e.at)}</td>
                              <td className="py-1.5 px-2 font-mono">
                                {e.source === "reprocessing" ? (
                                  <Link
                                    to="/production/reprocessing"
                                    className="text-primary underline"
                                    title="Rice reprocessing"
                                  >
                                    {e.dispatch_code || `#${e.dispatch_id}`}
                                  </Link>
                                ) : (
                                  <Link
                                    to="/sales/loading"
                                    className="text-primary underline"
                                    title={e.gate_pass_code || e.dispatch_code}
                                  >
                                    {e.dispatch_code || e.gate_pass_code || `#${e.dispatch_id}`}
                                  </Link>
                                )}
                              </td>
                              <td className="py-1.5 px-2 font-mono">{e.vehicle_number || "—"}</td>
                              <td className="py-1.5 px-2 max-w-[160px] truncate" title={e.party_name || ""}>
                                {e.party_name || "—"}
                              </td>
                              <td className="py-1.5 px-2">{e.state || "—"}</td>
                              <td className="py-1.5 px-2 text-right tabular font-medium">{e.bags}</td>
                              <td className="py-1.5 px-2 text-right tabular text-muted-foreground">{qtl(e.weight_qtl)}</td>
                              <td className="py-1.5 px-2">{e.loaded_by || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {Number(movements.totals?.bags_closing) < 0 && (
                <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                  Net is negative on this report line. Live loading still blocks overselling a batch;
                  check whether outs were attributed to this batch/pack while packing was logged on another
                  batch, or packing was deleted after dispatch.
                </p>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function StacksPage() {
  const { user } = useAuth();
  const stockDesk = isProductionLoadingSupervisorRole(user?.role);
  const { optionsFor } = useLookups();
  const varieties = optionsFor("paddy_variety");
  const list = useBootstrapList({
    path: "/stacks/bootstrap",
    columns: STACK_COLUMNS,
    defaultSort: { id: "stack_id", desc: false },
    wsTypes: ["stack"],
  });
  useBootstrapWS(["stack"], list.reload);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ stack_id: "", variety: "1509", capacity_qtl: "" });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stacks", mapNumberFields(form, ["capacity_qtl"]));
      toast.success("Stack created");
      setOpen(false);
      list.reload();
    } catch (err) { toast.error(extractError(err)); }
  };

  const title = list.table.hasActiveFilters
    ? `${list.meta.count} matching stacks`
    : `${list.meta.count} stacks`;

  if (stockDesk) {
    return (
      <div className="space-y-6 sm:space-y-8" data-testid="stacks-page">
        <PageHeader
          eyebrow="Packing & Loading"
          title="Stock on hand"
          description="Packed product stock by batch × pack size — Opening, In, Out, Closing (bags & qtl), including back-dated as-of."
        />
        <PackedProductStockPanel />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="stacks-page">
      <PageHeader
        eyebrow="Warehouse"
        title="Stacks & Inventory"
        description="Paddy stack heatmap plus packed product stock with Opening, In, Out, Closing (bags & qtl)."
        actions={
          <Button onClick={() => setOpen((v) => !v)} className="bg-bansal-sage hover:bg-bansal-sageDark text-white" data-testid="new-stack-button">
            <Plus className="h-4 w-4 mr-1" /> New Stack
          </Button>
        }
      />

      {open && (
        <SectionCard title="New Stack">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="stack-form">
            <Field label="Stack ID"><Input value={form.stack_id} onChange={(e) => setForm({ ...form, stack_id: e.target.value.toUpperCase() })} placeholder="S-D1" required data-testid="stack-id-input" /></Field>
            <Field label="Variety">
              <Select value={form.variety} onValueChange={(v) => setForm({ ...form, variety: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{varieties.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Capacity (qtl)"><Input type="number" value={form.capacity_qtl} onChange={(e) => setForm({ ...form, capacity_qtl: e.target.value })} /></Field>
            <div className="flex items-end">
              <Button type="submit" className="bg-bansal-sage hover:bg-bansal-sageDark text-white w-full" data-testid="stack-submit">Create</Button>
            </div>
          </form>
        </SectionCard>
      )}

      <Tabs defaultValue="packed">
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="packed" className="text-xs sm:text-sm">Packed product stock</TabsTrigger>
          <TabsTrigger value="paddy" className="text-xs sm:text-sm">Paddy stacks</TabsTrigger>
        </TabsList>

        <TabsContent value="packed" className="mt-4 space-y-4">
          <PackedProductStockPanel />
        </TabsContent>

        <TabsContent value="paddy" className="mt-4 space-y-6">
          <SectionCard title="Warehouse Heatmap (current page)" testId="heatmap">
            <DataTableBlock
              title=""
              wrapInSectionCard={false}
              columns={STACK_COLUMNS}
              table={list.table}
              testId="stacks-filters"
              showBody={false}
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 mt-4">
              {list.table.rows.map((s) => {
                const fill = Math.min(100, Math.round((s.current_qtl / s.capacity_qtl) * 100));
                const bg = fill === 0 ? "#F3F4F6" : `rgba(85, 107, 47, ${0.15 + (fill / 100) * 0.7})`;
                return (
                  <div key={s.stack_id} className="relative aspect-square rounded-sm border border-border overflow-hidden group cursor-default" style={{ background: bg }} data-testid={`stack-${s.stack_id}`}>
                    <div className="absolute inset-0 p-2 flex flex-col justify-between">
                      <div className="text-[10px] font-mono font-semibold" style={{ color: fill > 50 ? "white" : "#3a4a22" }}>{s.stack_id}</div>
                      <div>
                        <div className="font-serif text-lg" style={{ color: fill > 50 ? "white" : "#3a4a22" }}>{fill}%</div>
                        <div className="text-[9px] uppercase tracking-widest" style={{ color: fill > 50 ? "rgba(255,255,255,0.8)" : "#6b7f4a" }}>{s.variety}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          <DataTableBlock
            title={`Stack register (${title})`}
            columns={STACK_COLUMNS}
            table={list.table}
            loading={list.loading}
            totalCount={list.meta.count}
            testId="stacks-table"
            pagination={{ ...list.pagination, pageSizeOptions: LIST_PAGE_SIZE_OPTIONS }}
            exportButton={(
              <DataTableExportButton
                apiPath="/stacks"
                params={list.exportParams}
                columns={STACK_COLUMNS}
                filename="stacks.xlsx"
              />
            )}
            renderTrailingHeader=""
            renderRow={(s) => {
              const fill = Math.min(100, Math.round((s.current_qtl / s.capacity_qtl) * 100));
              return (
                <tr key={s.stack_id} className="border-t hover:bg-accent/60">
                  <td className="px-3 py-2.5 font-mono text-xs font-semibold">{s.stack_id}</td>
                  <td className="px-3 py-2.5">{s.variety}</td>
                  <td className="px-3 py-2.5 tabular">{s.current_qtl.toFixed(1)}</td>
                  <td className="px-3 py-2.5 tabular">{s.capacity_qtl}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-bansal-sage" style={{ width: `${fill}%` }} />
                      </div>
                      <span className="text-xs tabular">{fill}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><Pill tone={s.age_days > 60 ? "gold" : "default"}>{s.age_days}d</Pill></td>
                  <td className="px-3 py-2.5">
                    <AdminEntityActions
                      entityType="stack"
                      entityId={s.id}
                      entityLabel={s.stack_id}
                      onDeleted={list.reload}
                    />
                  </td>
                </tr>
              );
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
